'use client';
import React, { useRef, useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ComparisonRow {
  metric: string;
  before: string;
  after: string;
  win: 'speed' | 'money' | 'accuracy' | 'payment';
}

const rows: ComparisonRow[] = [
  {
    metric: 'Invoice creation time',
    before: '18–35 min per RO',
    after: '4.2 seconds',
    win: 'speed',
  },
  {
    metric: 'Parts markup accuracy',
    before: 'Manual — 61% accurate',
    after: 'Auto-matrix — 97.4%',
    win: 'accuracy',
  },
  {
    metric: 'Missed line items / month',
    before: 'Avg. 23 per shop',
    after: '< 1 per shop',
    win: 'accuracy',
  },
  {
    metric: 'Monthly billing recovered',
    before: '$0 — you don\'t know what you\'re missing',
    after: '+$38K avg. recovered',
    win: 'money',
  },
  {
    metric: 'Payment collection time',
    before: '3.9 days avg.',
    after: '1.8 days avg.',
    win: 'payment',
  },
  {
    metric: 'DMS export reconciliation',
    before: 'Manual — midnight ritual',
    after: 'Auto-sync — zero export',
    win: 'speed',
  },
  {
    metric: 'Mobile invoicing',
    before: 'Back to shop required',
    after: 'From the truck cab',
    win: 'speed',
  },
  {
    metric: 'Customer payment options',
    before: 'Cash / check / card on-site',
    after: 'SMS link, tap-to-pay, ACH',
    win: 'payment',
  },
];

const winColors = {
  speed: 'text-[#00E5CC]',
  money: 'text-[#00E5CC]',
  accuracy: 'text-[#00E5CC]',
  payment: 'text-[#00E5CC]',
};

const ComparisonTable: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-24 px-6">
      <div className="max-w-5xl mx-auto" ref={ref}>
        {/* Label */}
        <div className={`text-center mb-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-[11px] tracking-[0.4em] uppercase text-[rgba(192,199,208,0.5)] font-medium">
            The Inevitable Comparison
          </span>
          <h2 className="heading font-black text-white mt-3" style={{ fontSize: 'clamp(1.8rem, 4.5vw, 3rem)' }}>
            Your current process vs. Invoice
          </h2>
        </div>

        {/* Table */}
        <div
          className={`glass-panel overflow-hidden transition-all duration-700 delay-200 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Sticky header */}
          <div className="comparison-sticky-header grid grid-cols-12 bg-[rgba(13,13,13,0.9)] border-b border-[rgba(255,255,255,0.08)]">
            <div className="col-span-4 px-6 py-4">
              <span className="text-[11px] tracking-[0.3em] uppercase text-[rgba(192,199,208,0.4)] font-medium">Metric</span>
            </div>
            <div className="col-span-4 px-6 py-4 border-l border-[rgba(255,255,255,0.06)]">
              <span className="text-[11px] tracking-[0.3em] uppercase text-[rgba(192,199,208,0.5)] font-medium">Your Current Process</span>
            </div>
            <div className="col-span-4 px-6 py-4 border-l border-[rgba(0,229,204,0.2)] bg-[rgba(0,229,204,0.04)]">
              <span className="text-[11px] tracking-[0.3em] uppercase text-teal font-bold flex items-center gap-2">
                <Icon name="BoltIcon" size={12} className="text-teal" />
                With Invoice
              </span>
            </div>
          </div>

          {/* Rows */}
          {rows.map((row, i) => (
            <div
              key={i}
              className={`grid grid-cols-12 border-b border-[rgba(255,255,255,0.05)] last:border-0 transition-all duration-500 hover:bg-[rgba(255,255,255,0.02)] ${
                visible ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ transitionDelay: `${200 + i * 60}ms` }}
            >
              {/* Metric */}
              <div className="col-span-4 px-6 py-5 flex items-center">
                <span className="text-[13px] font-medium text-[#C0C7D0]">{row.metric}</span>
              </div>
              {/* Before */}
              <div className="col-span-4 px-6 py-5 border-l border-[rgba(255,255,255,0.05)] flex items-center">
                <span className="mono text-[12px] text-[rgba(192,199,208,0.5)]">{row.before}</span>
              </div>
              {/* After */}
              <div className="col-span-4 px-6 py-5 border-l border-[rgba(0,229,204,0.15)] bg-[rgba(0,229,204,0.03)] flex items-center gap-2">
                <Icon name="CheckCircleIcon" size={14} className="text-teal flex-shrink-0" variant="solid" />
                <span className={`mono text-[12px] font-semibold ${winColors[row.win]}`}>{row.after}</span>
              </div>
            </div>
          ))}
        </div>

        {/* CTA beneath table */}
        <div
          className={`mt-8 text-center transition-all duration-700 delay-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <p className="text-[#C0C7D0] text-sm mb-4">
            This comparison is built from real shop data across 14,000+ Invoice customers.
          </p>
          <a href="#calculator" className="btn-teal px-8 py-3.5 text-[14px] inline-block">
            Calculate My Shop&apos;s Numbers →
          </a>
        </div>
      </div>
    </section>
  );
};

export default ComparisonTable;